import { RoleName } from 'chaika-enums';
import { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useQuery } from '@tanstack/react-query';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { formatFullName } from '@utils/formatFullName';

import { queryClient } from '@store/queryClient';
import { store } from '@store';
import { primeUserListByIdsCache, userQueryOptions } from '@user/queries/userQueries';
import { findClinicUsers } from '@user/utils/findClinicUsers';

import {
    AsyncSelect,
    OptionType,
    NotSelectedOptionMasculine,
    AsyncSelectProps,
    AsyncMultiSelect,
} from '@components/Select';

interface Props extends Omit<AsyncSelectProps, 'onChange' | 'loadOptions' | 'selectedValue'> {
    isMultiSelect?: boolean;
    clearAfterSelect?: boolean;
    userId?: string | string[];
    organizationId?: string;
    firstUserId?: string;
    userRoles?: RoleName[];
    onChange(options: Nullable<OptionType> | Nullable<OptionType>[], name?: string): void;
    sortUsersWith?(userOptions: OptionType<string>[]): OptionType<string>[];
    filterUsersWith?(userOptions: OptionType[]): OptionType<string>[];
}

export const ClinicUserSelect = observer(function ClinicUserSelect(props: Props) {
    const {
        userId,
        defaultOptions,
        isMultiSelect = false,
        firstUserId,
        userRoles,
        sortUsersWith,
        filterUsersWith,
        organizationId,
        isClearable,
        onChange,
        ...rest
    } = props;

    const handleChange = useCallback<Props['onChange']>(
        (options, name) => {
            const ids = Array.isArray(options)
                ? options.map(opt => opt?.value).filter((v): v is string => typeof v === 'string')
                : typeof options?.value === 'string'
                  ? [options.value]
                  : [];
            primeUserListByIdsCache(queryClient, ids);
            onChange(options, name);
        },
        [onChange],
    );

    const putAuthUserAtFirst = useCallback(
        (options: OptionType[]): OptionType[] => {
            const authUserIndex = !!firstUserId ? options.findIndex(option => option.value === firstUserId) : undefined;

            return !!authUserIndex
                ? // changePositionInArray ломает поиск
                  options.reduce((acc, option) => {
                      if (option.value === firstUserId) {
                          acc.unshift(option);
                      } else {
                          acc.push(option);
                      }

                      return acc;
                  }, [] as OptionType[])
                : options;
        },
        [firstUserId],
    );

    const findUsers = useMemo(() => AwesomeDebouncePromise(findClinicUsers, 300), []);

    const loadUserOptionsFromUserDirectory = useCallback(
        async (name: string): Promise<OptionType<string>[]> => {
            const users = await findUsers({ name, organizationId, roleNames: userRoles });

            store.userList.addIfNotExist(users);

            return users.map(user => ({ value: user.id, label: formatFullName(user) }));
        },
        [findUsers, userRoles, organizationId],
    );

    const loadUserOptions = useCallback(
        async (name: string): Promise<OptionType[]> => {
            const options = await loadUserOptionsFromUserDirectory(name);

            const sortedOptions = !!sortUsersWith ? sortUsersWith(options) : putAuthUserAtFirst(options);

            return !!filterUsersWith ? filterUsersWith(sortedOptions) : sortedOptions;
        },
        [loadUserOptionsFromUserDirectory, sortUsersWith, filterUsersWith, putAuthUserAtFirst],
    );

    const selectedUserIds = useMemo<string[]>(() => {
        if (Array.isArray(userId)) {
            return userId;
        }
        if (typeof userId === 'string' && userId !== '') {
            return [userId];
        }
        return [];
    }, [userId]);

    const { data: selectedUsers = [] } = useQuery({
        ...userQueryOptions.list({ ids: selectedUserIds }),
        enabled: selectedUserIds.length > 0,
    });

    const selectedOptions = useMemo<OptionType<string>[]>(
        () =>
            selectedUsers.map(user => ({
                value: user.id,
                label: formatFullName(user),
            })),
        [selectedUsers],
    );

    const makeDefaultOptions = (defaultOpts?: OptionType[]): OptionType[] => {
        const defaultOpt = defaultOpts ?? [];
        const existingValues = new Set(defaultOpt.map(opt => opt.value));
        const uniqueUserOptions = selectedOptions.filter(opt => !existingValues.has(opt.value));
        const defaultOptionsWithSelectedUser = [...uniqueUserOptions, ...defaultOpt];

        return Array.isArray(defaultOpts)
            ? putAuthUserAtFirst(defaultOptionsWithSelectedUser)
            : isClearable
              ? [NotSelectedOptionMasculine, ...selectedOptions]
              : selectedOptions;
    };

    const passThroughProps = {
        ...rest,
        firstUserId,
        userRoles,
        sortUsersWith,
        filterUsersWith,
        organizationId,
        isClearable,
        onChange: handleChange,
    };

    return isMultiSelect ? (
        <AsyncMultiSelect
            {...passThroughProps}
            dataTest={passThroughProps.dataTest ?? 'ClinicUserSelect'}
            value={passThroughProps.value as OptionType[] | undefined}
            loadOptions={loadUserOptions}
            selectedValues={userId as string[]}
            defaultOptions={makeDefaultOptions(Array.isArray(defaultOptions) ? defaultOptions : undefined)}
        />
    ) : (
        <AsyncSelect
            {...passThroughProps}
            loadOptions={loadUserOptions}
            selectedValue={userId as string}
            defaultOptions={typeof defaultOptions === 'boolean' ? defaultOptions : makeDefaultOptions(defaultOptions)}
        />
    );
});
